import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEntityBasePath } from "../../../../../../utils/entityPaths";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete
} from "@mui/material";
import { CommentDto, Broker } from "../../../../../../api/Api";
import {
  useBrokerAssignees,
  useBrokerProgressOverride,
  useUpdateBroker
} from "../../../../dataHooks/brokerQueries";
import { createLifecycleActions } from "../../../../../../components/lifecycle/createLifecycleActions";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useLifecycleComments } from "../../../../../../components/lifecycle/useLifecycleComments";
import { RemitReportingReadOnly } from "../../../../../../components/lifecycle/remitReportingReadOnly";
import { brokerAttachmentApi } from "../../../brokerAttachmentApi";
import { useRoles } from "../../../../../../auth/useRoles";
import { ownsLifecycle } from "../../../../../../auth/permissions";

type Props = {
  specificBroker: Broker;
};

type FormData = {
  agreementReview: string;
  agreementReviewAssignee: string;
  brokerSetup: string;
  brokerSetupAssignee: string;
  iban: string;
  ibanAssignee: string;
  callback: string;
  callbackAssignee: string;
  tradeConfirmationSetup: string;
  comments: CommentDto[];
  signOffNote: string;
};

export const Finance: FC<Props> = ({ specificBroker }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const basePath = useEntityBasePath("broker");
  const roles = useRoles();
  const progressOverride = useBrokerProgressOverride(specificBroker, "finance");

  const financeAssignees = useBrokerAssignees("finance");

  const [formData, setFormData] = useState<FormData>({
    agreementReview: specificBroker?.financeLifecycle?.agreementReview || "",
    agreementReviewAssignee:
      specificBroker?.financeLifecycle?.agreementReviewAssignee || "",
    brokerSetup: specificBroker?.financeLifecycle?.brokerSetup || "",
    brokerSetupAssignee:
      specificBroker?.financeLifecycle?.brokerSetupAssignee || "",
    iban: specificBroker?.financeLifecycle?.iban || "",
    ibanAssignee: specificBroker?.financeLifecycle?.ibanAssignee || "",
    callback: specificBroker?.financeLifecycle?.callback || "",
    callbackAssignee: specificBroker?.financeLifecycle?.callbackAssignee || "",
    tradeConfirmationSetup:
      specificBroker?.financeLifecycle?.tradeConfirmationSetup || "",
    comments: specificBroker?.financeLifecycle?.comments || [],
    signOffNote: specificBroker?.financeLifecycle?.signOffNote || ""
  });

  const handleClose = () => {
    navigate(`${basePath}/${params.id}`);
  };

  const updateBroker = useUpdateBroker({ onSuccess: handleClose });
  const commentUpdate = useUpdateBroker();

  const buildBroker = (values: FormData & { complete?: boolean }): Broker => ({
    ...specificBroker,
    financeLifecycle: {
      ...specificBroker.financeLifecycle,
      ...values
    }
  });

  const {
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment,
    isSaving
  } = useLifecycleComments({
    formData,
    setFormData,
    update: commentUpdate,
    buildEntityWithComments: (comments) =>
      buildBroker({ ...formData, comments })
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const { handleSave, handleComplete } = createLifecycleActions({
    formData,
    currentComment,
    currentProgress: specificBroker.progress?.finance,
    canSetInProgress: ownsLifecycle(roles, "finance"),
    buildEntity: (values, progress) => {
      const saved = buildBroker(values);
      return {
        ...saved,
        progress: { ...saved.progress, finance: progress }
      };
    },
    mutate: updateBroker.mutate
  });

  const open = location.pathname.includes("finance");

  const canComplete =
    formData.agreementReview && formData.brokerSetup && formData.callback;

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Finance"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={brokerAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: handleSave,
        onComplete: handleComplete,
        canComplete: !!canComplete,
        isSaving: updateBroker.isPending
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>
              Agreement review (invoicing and settlement terms)
            </InputLabel>
            <Select
              label="Agreement review (invoicing and settlement terms)"
              value={formData.agreementReview}
              onChange={(e) => handleChange("agreementReview", e.target.value)}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.agreementReviewAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("agreementReviewAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agreement review assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Broker setup in Business Central</InputLabel>
            <Select
              label="Broker setup in Business Central"
              value={formData.brokerSetup}
              onChange={(e) => handleChange("brokerSetup", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.brokerSetupAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("brokerSetupAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Broker setup assignee"
                size="medium"
              />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <TextField
            sx={{ flex: 1 }}
            label="IBAN"
            size="medium"
            value={formData.iban}
            onChange={(e) => handleChange("iban", e.target.value)}
          />

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.ibanAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("ibanAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="IBAN assignee" size="medium" />
            )}
          />
        </Box>

        <Box display="flex" gap={2}>
          <FormControl required size="medium" sx={{ flex: 1 }}>
            <InputLabel>Callback</InputLabel>
            <Select
              label="Callback"
              value={formData.callback}
              onChange={(e) => handleChange("callback", e.target.value)}
            >
              <MenuItem value="initiated">Initiated</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_needed">Not needed</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={financeAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              financeAssignees.data?.find(
                (a) => a.email === formData.callbackAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange("callbackAssignee", newValue?.email || "")
            }
            renderInput={(params) => (
              <TextField {...params} label="Callback assignee" size="medium" />
            )}
          />
        </Box>

        {specificBroker.marketAccessLifecycle?.tradeConfirmation ===
          "needed" && (
          <FormControl required size="medium">
            <InputLabel>Trade Confirmation Setup</InputLabel>
            <Select
              label="Trade Confirmation Setup"
              value={formData.tradeConfirmationSetup}
              onChange={(e) =>
                handleChange("tradeConfirmationSetup", e.target.value)
              }
            >
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="not_completed">Not completed</MenuItem>
            </Select>
          </FormControl>
        )}

        <RemitReportingReadOnly
          complianceLifecycle={specificBroker.complianceLifecycle}
        />

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) =>
            setFormData((prev) => ({ ...prev, signOffNote: value }))
          }
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
